import React from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';

/**
 * A moldura de um bloco de leitura.
 *
 * A referência é documento de consultoria impresso, não painel de BI: cada
 * bloco abre com a PERGUNTA que ele responde, em peso regular — ela é convite à
 * leitura, não manchete —, e o conteúdo vem abaixo com uma ideia por dobra.
 *
 * `acao` fica no canto direito do cabeçalho porque no celular ela desce para a
 * linha seguinte sem quebrar o título.
 *
 * A SUPERFÍCIE É VIDRO, e a receita mora no tema (`MuiCard`), não aqui: o filete
 * especular do topo, o desfoque do fundo e o reflexo que segue o ponteiro valem
 * para todo `Card` do produto. O que este arquivo acrescenta é a ENTRADA — o
 * bloco sobe 12px e assenta.
 *
 * A ENTRADA É NA MONTAGEM, E NÃO NA DOBRA — e isso foi medido, não escolhido.
 * Com `whileInView`, os blocos abaixo da primeira dobra ficavam em `opacity: 0`
 * até alguém rolar até eles: a captura de página inteira saiu com a tela de
 * Saúde mostrando só dois blocos e 2.600px de vazio, e um leitor que chegasse
 * com JavaScript lento veria o mesmo. Conteúdo financeiro não pode depender de
 * um observador de interseção para existir. Aqui o bloco entra uma vez, junto
 * com a tela; o que acontece fora da vista custa nada e nunca esconde número.
 */
const Bloco = ({ titulo, pergunta, acao, nota, children, sx }) => (
  <Card
    component={motion.div}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
    variant="outlined"
    sx={{
      p: { xs: 2, sm: 3 },
      '&:hover': {
        borderColor: (t) =>
          t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(15,23,34,0.14)'
      },
      ...sx
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1}
      sx={{ mb: pergunta || children ? 2 : 0 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" component="h2" sx={{ lineHeight: 1.25 }}>
          {titulo}
        </Typography>
        {pergunta && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '65ch' }}>
            {pergunta}
          </Typography>
        )}
      </Box>
      {acao && <Box sx={{ flexShrink: 0 }}>{acao}</Box>}
    </Stack>

    {children}

    {nota && (
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ mt: 2, display: 'block', maxWidth: '75ch' }}
      >
        {nota}
      </Typography>
    )}
  </Card>
);

export default Bloco;
